import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  FontColorEnum,
  FormField,
  FormHint,
  FormLabel,
  IconSizeEnum,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldError,
  FieldValues,
  Path,
  UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NumberIntoHHMM, NumberIntoHHMMSS } from '@/utils/hh-mm-ss-into-number';

type TInputTimeV3Props<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  seconds?: boolean;
  classname?: string;
  max?: number;
};

function hmsToSeconds(h: number, m: number, s: number) {
  return h * 3600 + m * 60 + s;
}

function buildTimeString(hours: number, minutes: number, seconds: number, withSeconds?: boolean) {
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  if (withSeconds) {
    const ss = String(seconds).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  }
  return `${hh}:${mm}`;
}

type TInputTimeV3FieldProps = Pick<
  TInputTimeV3Props<FieldValues>,
  'label' | 'disabled' | 'placeholder' | 'seconds' | 'classname' | 'max'
> & {
  value: string;
  onChange: ControllerRenderProps['onChange'];
  error?: FieldError;
};

function InputTimeV3Field({
  value,
  onChange,
  error,
  disabled,
  label,
  seconds,
  placeholder,
  classname,
  max,
}: TInputTimeV3FieldProps) {
  // Estados internos para horas, minutos y (opcional) segundos
  const [hours, setHours] = useState('00');
  const [minutes, setMinutes] = useState('00');
  const [secs, setSecs] = useState('00');
  const [showSelectedValues, setShowSelectedValues] = useState(false);

  // Cada vez que cambie "value" en el form, parseamos para sincronizar
  useEffect(() => {
    if (value) {
      const parts = value.split(':');
      if (parts.length >= 2) {
        setHours(parts[0] ?? '00');
        setMinutes(parts[1] ?? '00');
      }
      if (seconds && parts.length === 3) {
        setSecs(parts[2] ?? '00');
      }
    } else {
      // Si no hay valor, reseteamos
      setHours('00');
      setMinutes('00');
      setSecs('00');
    }
  }, [value, seconds]);

  // Función para clipear valores en un rango
  const clamp = (val: number, min: number, max: number) => {
    return Math.min(Math.max(val, min), max);
  };

  const handleHoursChange = (newHours: number) => {
    // Ajusta para que no se salga de 0-23, si así lo deseas. O podrías dejarlo sin límite.
    const clampedHours = clamp(newHours, 0, 23);
    setHours(String(clampedHours).padStart(2, '0'));

    // Comprobamos si excede max
    const total = hmsToSeconds(clampedHours, parseInt(minutes, 10), parseInt(secs, 10));
    if (max && total > max) {
      // Si excede, fijamos el valor en "max"
      const newVal = seconds ? NumberIntoHHMMSS(max) : NumberIntoHHMM(Math.floor(max));
      onChange(newVal);

      // Volvemos a parsear para re-sincronizar estados
      const [hh, mm, ss] = newVal.split(':');
      setHours(hh ?? '00');
      setMinutes(mm ?? '00');
      if (seconds && ss) setSecs(ss);
      return;
    }

    // Si no excede, generamos el nuevo string
    onChange(buildTimeString(clampedHours, parseInt(minutes, 10), parseInt(secs, 10), seconds));
  };

  const handleMinutesChange = (newMinutes: number) => {
    const clampedMinutes = clamp(newMinutes, 0, 59);
    setMinutes(String(clampedMinutes).padStart(2, '0'));

    const total = hmsToSeconds(parseInt(hours, 10), clampedMinutes, parseInt(secs, 10));
    if (max && total > max) {
      const newVal = seconds ? NumberIntoHHMMSS(max) : NumberIntoHHMM(Math.floor(max));
      onChange(newVal);

      const [hh, mm, ss] = newVal.split(':');
      setHours(hh ?? '00');
      setMinutes(mm ?? '00');
      if (seconds && ss) setSecs(ss);
      return;
    }

    onChange(buildTimeString(parseInt(hours, 10), clampedMinutes, parseInt(secs, 10), seconds));
  };

  const handleSecondsChange = (newSeconds: number) => {
    const clampedSeconds = clamp(newSeconds, 0, 59);
    setSecs(String(clampedSeconds).padStart(2, '0'));

    const total = hmsToSeconds(parseInt(hours, 10), parseInt(minutes, 10), clampedSeconds);
    if (max && total > max) {
      const newVal = NumberIntoHHMMSS(max);
      onChange(newVal);

      const [hh, mm, ss] = newVal.split(':');
      setHours(hh ?? '00');
      setMinutes(mm ?? '00');
      setSecs(ss ?? '00');
      return;
    }

    onChange(buildTimeString(parseInt(hours, 10), parseInt(minutes, 10), clampedSeconds, seconds));
  };

  // Botones de incremento/decremento
  const incrementHours = () => handleHoursChange(parseInt(hours, 10) + 1);
  const decrementHours = () => handleHoursChange(parseInt(hours, 10) - 1);

  const incrementMinutes = () => handleMinutesChange(parseInt(minutes, 10) + 1);
  const decrementMinutes = () => handleMinutesChange(parseInt(minutes, 10) - 1);

  const incrementSeconds = () => handleSecondsChange(parseInt(secs, 10) + 1);
  const decrementSeconds = () => handleSecondsChange(parseInt(secs, 10) - 1);

  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (divRef.current && !divRef.current.contains(e.target as Node)) {
        setShowSelectedValues(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [divRef]);

  const handleClickInput = () => {
    setShowSelectedValues(prev => !prev);
  };

  return (
    <div className="flex w-full flex-col gap-2" ref={divRef}>
      <div
        onClick={handleClickInput}
        className="flex min-h-[48px] w-full flex-col items-center gap-2 self-stretch rounded-size-0-12 border-b-[1px] border-stroke-30-0 bg-neutral-00 p-size-12 text-text-medium-10 hover:border-stroke-60-0 hover:text-text-dark-10"
      >
        <input
          type="time"
          disabled={disabled}
          max={max ? NumberIntoHHMMSS(max) : undefined}
          value={value}
          placeholder={label || placeholder}
          onChange={onChange}
          step={seconds ? 1 : 60}
          className={clsx(
            '!hide-time-icon w-full appearance-none border-none bg-transparent p-0 text-caption font-regular text-inherit shadow-none outline-none !ring-0',
            !!error && '!border-stroke-error-1 !bg-stroke-error-1/20 !text-text-static-medium-dark'
          )}
        />
      </div>
      {/* Contenedor principal del “spinner” */}
      {showSelectedValues && (
        <div
          className={clsx(
            'flex w-full flex-1 items-center justify-center gap-3 bg-[#F7F9FE] py-2',
            classname
          )}
        >
          {/* Horas */}
          <ClockIcon size={IconSizeEnum['2XL']} className="text-accent-active-primary-30" />
          <div className="flex max-w-[20px] flex-col">
            <button
              type="button"
              onClick={incrementHours}
              disabled={disabled}
              className="flex justify-center"
            >
              <ChevronUpIcon className="text-text-medium-10" size={IconSizeEnum.SM} />
            </button>
            <input
              type="number"
              disabled={disabled}
              value={hours}
              onChange={e => handleHoursChange(Number(e.target.value))}
              className="no-spinner !focus:ring-0 !border-none !bg-transparent !p-0 !ring-0"
            />
            <button
              type="button"
              onClick={decrementHours}
              disabled={disabled}
              className="flex justify-center"
            >
              <ChevronDownIcon className="text-text-medium-10" size={IconSizeEnum.SM} />
            </button>
          </div>

          <span>:</span>

          {/* Minutos */}
          <div className="flex max-w-[20px] flex-col">
            <button
              type="button"
              onClick={incrementMinutes}
              disabled={disabled}
              className="flex justify-center"
            >
              <ChevronUpIcon className="text-text-medium-10" size={IconSizeEnum.SM} />
            </button>
            <input
              type="number"
              disabled={disabled}
              value={minutes}
              onChange={e => handleMinutesChange(Number(e.target.value))}
              className="no-spinner !focus:ring-0 !border-none !bg-transparent !p-0 !ring-0"
            />
            <button
              type="button"
              onClick={decrementMinutes}
              disabled={disabled}
              className="flex justify-center"
            >
              <ChevronDownIcon className="text-text-medium-10" size={IconSizeEnum.SM} />
            </button>
          </div>

          {/* Segundos (opcional) */}
          {seconds && (
            <>
              <span>:</span>
              <div className="flex max-w-[20px] flex-col">
                <button
                  type="button"
                  onClick={incrementSeconds}
                  disabled={disabled}
                  className="flex justify-center"
                >
                  <ChevronUpIcon className="text-text-medium-10" size={IconSizeEnum.SM} />
                </button>
                <input
                  type="number"
                  disabled={disabled}
                  value={secs}
                  onChange={e => handleSecondsChange(Number(e.target.value))}
                  className="no-spinner !focus:ring-0 !border-none !bg-transparent !p-0 !ring-0"
                />
                <button
                  type="button"
                  onClick={decrementSeconds}
                  disabled={disabled}
                  className="flex justify-center"
                >
                  <ChevronDownIcon className="text-text-medium-10" size={IconSizeEnum.SM} />
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Mensaje de error si existe */}
      {error && (
        <FormHint color={FontColorEnum.Error20} type="error">
          {error.message}
        </FormHint>
      )}
    </div>
  );
}

export function InputTimeV3<T extends FieldValues>({
  form,
  name,
  required,
  disabled,
  label,
  seconds,
  placeholder,
  classname,
  max,
}: TInputTimeV3Props<T>) {
  const { t } = useTranslation();
  const { control } = form;

  return (
    <FormField>
      {label && <FormLabel htmlFor={name}>{label}</FormLabel>}

      <Controller
        name={name}
        control={control}
        rules={{
          required: required ? { value: true, message: t('El campo es obligatorio') } : false,
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <InputTimeV3Field
            value={value}
            onChange={onChange}
            error={error}
            disabled={disabled}
            label={label}
            seconds={seconds}
            placeholder={placeholder}
            classname={classname}
            max={max}
          />
        )}
      />
    </FormField>
  );
}
